import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import useTokenStore from "../../stores/tokens";
import { compatApi } from "../../utils/Intercepter";
import { useFilteredPagination } from "../../utils/PageUtil";
import { PURPLE, MINT, hexToRgba } from "../../utils/designUtils";

// ─── Status badge ────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
    const styles = {
        "진행중": { color: MINT, bg: hexToRgba(MINT, 0.14), dot: MINT },
        "예정": { color: PURPLE, bg: hexToRgba(PURPLE, 0.14), dot: PURPLE },
        "종료": { color: "#8b8a99", bg: "#ececf2", dot: "#aaa9b7" },
    }[status] || { color: "#5d5b6e", bg: "#f4f3f9", dot: "#aaa9b7" };

    return (
        <span style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            fontSize: 11.5, fontWeight: 600, color: styles.color,
            background: styles.bg, padding: "3px 9px", borderRadius: 999,
            whiteSpace: "nowrap", lineHeight: 1.5,
        }}>
            <span style={{ width: 5, height: 5, borderRadius: "50%", background: styles.dot, flexShrink: 0 }} />
            {status}
        </span>
    );
}

// ─── Filter checkbox ─────────────────────────────────────────────────────────
function FilterCheck({ label, checked, onToggle, disabled }) {
    return (
        <label onClick={!disabled ? onToggle : undefined} style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            cursor: disabled ? "not-allowed" : "pointer", fontSize: 13,
            color: disabled ? "#aaa" : "#3a3849", userSelect: "none",
            opacity: disabled ? 0.5 : 1,
        }}>
            <span style={{
                width: 18, height: 18, borderRadius: 5, flexShrink: 0,
                background: checked ? MINT : "white",
                border: `1.5px solid ${checked ? MINT : "#d0cfdb"}`,
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                transition: "all 140ms",
            }}>
                {checked && (
                    <svg width="12" height="12" viewBox="0 0 12 12">
                        <path d="M2 6.5L5 9.5L10 3.5" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                )}
            </span>
            {label}
        </label>
    );
}

function td(align = "left", muted = false) {
    return {
        padding: "12px 12px",
        textAlign: align,
        borderBottom: "1px solid #f4f3f9",
        fontSize: 12.5,
        color: muted ? "#5d5b6e" : "#3a3849",
        fontVariantNumeric: "tabular-nums",
        whiteSpace: "nowrap",
    };
}

const SEARCH_OPTIONS = [
    { value: "projectName", label: "프로젝트 명" },
    { value: "client", label: "고객사" },
    { value: "status", label: "상태" },
];

// ─── Main component ───────────────────────────────────────────────────────────
export default function ProjectList() {
    const { user } = useTokenStore();
    const navigation = useNavigate();

    const [allProjectList, setAllProjectList] = useState([]);
    const [projectList, setProjectList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [myProjectIds, setMyProjectIds] = useState(new Set());
    const [searchWord, setSearchWord] = useState("");
    const [searchOption, setSearchOption] = useState("projectName");
    const [showActive, setShowActive] = useState(true);
    const [showCompleted, setShowCompleted] = useState(false);
    const [filterOpen, setFilterOpen] = useState(false);

    const {
        currentItems, currentPage, totalPages,
        startPage, endPage,
        handlePageChange, handleNextGroup, handlePrevGroup,
        applySort, sortConfig, updateOriginalData
    } = useFilteredPagination(projectList, null, 10, 5, { key: '', direction: 'asc' });

    const applyCustomSort = (data) => {
        return [...data].sort((a, b) => {
            const statusOrder = { '진행중': 0, '예정': 1, '종료': 2 };
            if (statusOrder[a.status] !== statusOrder[b.status]) {
                return statusOrder[a.status] - statusOrder[b.status];
            }
            const da = new Date(a.contractualStartDate);
            const db = new Date(b.contractualStartDate);
            if (a.status === '진행중') return db - da;
            if (a.status === '예정') return da - db;
            if (a.status === '종료') return db - da;
            return 0;
        });
    };

    const calculateStatus = (contractualStartDate, contractualEndDate, actualStartDate, actualEndDate) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const startDate = actualStartDate ? new Date(actualStartDate) : new Date(contractualStartDate);
        const endDate = actualEndDate ? new Date(actualEndDate) : new Date(contractualEndDate);
        if (today < startDate) return '예정';
        else if (startDate <= today && today <= endDate) return '진행중';
        else return '종료';
    };

    useEffect(() => {
        async function initialize() {
            if (!user?.currentOrganizationCode) return;
            setLoading(true);
            try {
                const response = await compatApi.post('/project/readProjectList', user.currentOrganizationCode);
                if (response.ok) {
                    const dataWithStatus = (response.data || []).map(project => ({
                        ...project,
                        status: calculateStatus(project.contractualStartDate, project.contractualEndDate, project.startDate, project.endDate),
                    }));
                    setAllProjectList(dataWithStatus);
                }
                const myProjectResponse = await compatApi.post('/project/readMyProjectList', user.currentOrganizationCode, user?.userEmail);
                if (myProjectResponse.ok) {
                    setMyProjectIds(new Set((myProjectResponse.data || []).map(p => p.projectId)));
                }
            } catch (error) { console.error('fetchData 오류:', error); }
            finally { setLoading(false); }
        }
        initialize();
    }, [user?.currentOrganizationCode, user?.userEmail]);

    useEffect(() => {
        let filtered = allProjectList.filter(project => {
            if (showActive && (project.status === '진행중' || project.status === '예정')) return true;
            if (showCompleted && project.status === '종료') return true;
            return false;
        });
        const sorted = applyCustomSort(filtered);
        setProjectList(sorted);
        updateOriginalData(sorted);
    }, [showActive, showCompleted, allProjectList]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSearch = () => {
        if (searchOption === 'status') {
            const filteredByStatus = allProjectList.filter(project =>
                searchWord === "" ? true : project.status === searchWord
            );
            const filteredByCheckboxes = filteredByStatus.filter(project => {
                if (showActive && (project.status === '진행중' || project.status === '예정')) return true;
                if (showCompleted && project.status === '종료') return true;
                return false;
            });
            const sorted = applyCustomSort(filteredByCheckboxes);
            setProjectList(sorted);
            updateOriginalData(sorted);
            handlePageChange(1);
            return;
        }
        compatApi.post('/project/search', {
            organizationCode: user?.currentOrganizationCode || '',
            searchWord: searchWord || '',
            searchOption
        }).then(response => {
            if (response.ok) {
                const dataWithStatus = (response.data || []).map(project => ({
                    ...project,
                    status: calculateStatus(project.contractualStartDate, project.contractualEndDate, project.startDate, project.endDate),
                }));
                setAllProjectList(dataWithStatus);
                handlePageChange(1);
            } else {
                console.error('검색 실패');
                alert('검색 결과가 없습니다.');
            }
        }).catch(error => { console.error('API 오류:', error); });
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return '';
        return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
    };

    if (loading) {
        return (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "calc(100vh - 61px)", background: "#f5f4fa" }}>
                <div style={{
                    width: 40, height: 40, borderRadius: "50%",
                    border: `3px solid ${hexToRgba(PURPLE, 0.2)}`,
                    borderTopColor: PURPLE,
                    animation: "spin 0.7s linear infinite",
                }} />
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
        );
    }

    const activeCount = allProjectList.filter(p => p.status === '진행중').length;
    const scheduledCount = allProjectList.filter(p => p.status === '예정').length;
    const endedCount = allProjectList.filter(p => p.status === '종료').length;
    const currentLabel = SEARCH_OPTIONS.find(o => o.value === searchOption)?.label || "프로젝트 명";

    return (
        <div style={{ background: "#f5f4fa", minHeight: "calc(100vh - 61px)" }}>
            <main style={{ maxWidth: 1600, margin: "0 auto", padding: "32px clamp(20px, 3vw, 40px) 48px" }}>

                {/* Page header */}
                <div style={{ marginBottom: 20 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, fontSize: 12, color: "#8b8a99" }}>
                        <span>프로젝트</span>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                            <path d="M9 18l6-6-6-6" />
                        </svg>
                        <span style={{ color: "#3a3849", fontWeight: 500 }}>프로젝트 목록</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
                        <div>
                            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: "#1f1d2e", letterSpacing: "-0.01em" }}>
                                프로젝트 목록
                            </h1>
                            <p style={{ margin: "4px 0 0", fontSize: 13.5, color: "#7a7989" }}>
                                전체 프로젝트 현황을 조회하고 관리할 수 있습니다.
                            </p>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: "#5d5b6e" }}>
                            <span style={{ width: 8, height: 8, borderRadius: "50%", background: MINT, display: "inline-block" }} />
                            <span>진행중 <b style={{ color: "#1f1d2e", marginLeft: 2 }}>{activeCount}</b></span>
                            <span style={{ width: 8, height: 8, borderRadius: "50%", background: PURPLE, display: "inline-block", marginLeft: 8 }} />
                            <span>예정 <b style={{ color: "#1f1d2e", marginLeft: 2 }}>{scheduledCount}</b></span>
                            <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#cdc8d6", display: "inline-block", marginLeft: 8 }} />
                            <span>종료 <b style={{ color: "#1f1d2e", marginLeft: 2 }}>{endedCount}</b></span>
                        </div>
                    </div>
                </div>

                {/* Search & filter row */}
                <div style={{
                    background: "white", borderRadius: 16, padding: "16px 20px",
                    border: "1px solid #f0eff5",
                    boxShadow: "0 1px 2px rgba(60,50,120,.04)",
                    marginBottom: 16,
                    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap",
                }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
                        <FilterCheck
                            label="진행중 / 예정 프로젝트"
                            checked={showActive}
                            onToggle={() => setShowActive(!showActive)}
                            disabled={searchOption === 'status'}
                        />
                        <FilterCheck
                            label="종료된 프로젝트"
                            checked={showCompleted}
                            onToggle={() => setShowCompleted(!showCompleted)}
                            disabled={searchOption === 'status'}
                        />
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        {/* Filter type dropdown */}
                        <div style={{ position: "relative" }}>
                            <button
                                onClick={() => setFilterOpen(!filterOpen)}
                                style={{
                                    height: 38, padding: "0 30px 0 14px",
                                    borderRadius: 10, background: "#fafaff",
                                    border: "1.5px solid #ececf2",
                                    fontSize: 13, color: "#3a3849", fontWeight: 500,
                                    cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
                                    position: "relative", display: "inline-flex", alignItems: "center",
                                }}
                            >
                                {currentLabel}
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#8b8a99" strokeWidth="2.5" strokeLinecap="round"
                                    style={{ position: "absolute", right: 9, top: "50%", transform: "translateY(-50%)" }}>
                                    <path d="M6 9l6 6 6-6" />
                                </svg>
                            </button>
                            {filterOpen && (
                                <>
                                    <div onClick={() => setFilterOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 5 }} />
                                    <div style={{
                                        position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 6,
                                        background: "white", borderRadius: 10,
                                        boxShadow: "0 8px 24px -8px rgba(60,50,120,.20)",
                                        border: "1px solid #ececf2", minWidth: 140, overflow: "hidden",
                                    }}>
                                        {SEARCH_OPTIONS.map(opt => (
                                            <button key={opt.value} onClick={() => {
                                                setSearchOption(opt.value);
                                                setSearchWord('');
                                                setFilterOpen(false);
                                                if (opt.value === 'status') {
                                                    setShowActive(true);
                                                    setShowCompleted(true);
                                                }
                                            }} style={{
                                                display: "block", width: "100%", textAlign: "left",
                                                padding: "9px 14px",
                                                background: searchOption === opt.value ? hexToRgba(PURPLE, 0.08) : "white",
                                                border: "none",
                                                color: searchOption === opt.value ? PURPLE : "#3a3849",
                                                fontSize: 13,
                                                fontWeight: searchOption === opt.value ? 600 : 500,
                                                cursor: "pointer", fontFamily: "inherit",
                                            }}>{opt.label}</button>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>

                        {searchOption === 'status' ? (
                            <select
                                value={searchWord}
                                onChange={(e) => setSearchWord(e.target.value)}
                                style={{
                                    height: 38, width: 200, borderRadius: 10, padding: "0 12px",
                                    border: "1.5px solid #ececf2", background: "white",
                                    fontSize: 13, color: "#1f1d2e", outline: "none", fontFamily: "inherit",
                                    boxShadow: "none",
                                }}
                            >
                                <option value="">전체</option>
                                <option value="예정">예정</option>
                                <option value="진행중">진행중</option>
                                <option value="종료">종료</option>
                            </select>
                        ) : (
                            <input
                                type="text"
                                value={searchWord}
                                onChange={(e) => setSearchWord(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                                placeholder="검색어를 입력 하세요"
                                style={{
                                    height: 38, width: 280, borderRadius: 10, padding: "0 14px",
                                    border: "1.5px solid #ececf2", background: "white",
                                    fontSize: 13, color: "#1f1d2e", outline: "none",
                                    fontFamily: "inherit", boxShadow: "none",
                                }}
                                onFocus={(e) => { e.target.style.borderColor = PURPLE; e.target.style.boxShadow = `0 0 0 4px ${hexToRgba(PURPLE, 0.10)}`; }}
                                onBlur={(e) => { e.target.style.borderColor = "#ececf2"; e.target.style.boxShadow = "none"; }}
                            />
                        )}

                        <button
                            onClick={handleSearch}
                            style={{
                                height: 38, padding: "0 22px", borderRadius: 10, border: "none",
                                background: `linear-gradient(135deg, ${PURPLE}, ${MINT})`,
                                color: "white", fontSize: 13, fontWeight: 600,
                                cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
                                boxShadow: `0 6px 14px -6px ${hexToRgba(PURPLE, 0.55)}`,
                                display: "inline-flex", alignItems: "center", gap: 6,
                            }}
                        >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" />
                            </svg>
                            검색
                        </button>
                    </div>
                </div>

                {/* Main card */}
                <div style={{
                    background: "white", borderRadius: 16,
                    border: "1px solid #f0eff5",
                    boxShadow: "0 1px 2px rgba(60,50,120,.04), 0 4px 16px -8px rgba(60,50,120,.08)",
                    overflow: "hidden",
                }}>
                    {/* Card header */}
                    <div style={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        padding: "16px 20px",
                        background: "linear-gradient(180deg, #faf9fd 0%, #f5f4fa 100%)",
                        borderBottom: "1px solid #ececf2",
                        flexWrap: "wrap", gap: 10,
                    }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <h2 style={{ margin: 0, fontSize: 14.5, fontWeight: 600, color: "#1f1d2e" }}>프로젝트 목록</h2>
                            <span style={{
                                fontSize: 11, fontWeight: 600,
                                background: "white", color: "#5d5b6e",
                                padding: "2px 8px", borderRadius: 999, lineHeight: 1.45,
                                border: "1px solid #ececf2",
                            }}>총 {projectList.length}건</span>
                        </div>
                        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                            <button
                                onClick={() => navigation('/gantt-chart')}
                                style={{
                                    height: 34, padding: "0 14px", borderRadius: 8,
                                    background: "white", border: `1.5px solid ${MINT}`,
                                    color: MINT, fontSize: 12.5, fontWeight: 600,
                                    cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
                                    display: "inline-flex", alignItems: "center", gap: 6,
                                }}
                            >
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" />
                                    <rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" />
                                </svg>
                                목록 차트 보기
                            </button>
                            {user?.currentRole === 'MASTER' && (
                                <button
                                    onClick={() => navigation('/project/create')}
                                    style={{
                                        height: 34, padding: "0 14px", borderRadius: 8,
                                        background: `linear-gradient(135deg, ${PURPLE}, ${MINT})`,
                                        border: "none", color: "white", fontSize: 12.5, fontWeight: 600,
                                        cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
                                        display: "inline-flex", alignItems: "center", gap: 4,
                                        boxShadow: `0 4px 10px -4px ${hexToRgba(PURPLE, 0.5)}`,
                                    }}
                                >
                                    + 프로젝트 추가
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Table */}
                    <div className="tbl-scroll" style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", minWidth: 1240, borderCollapse: "separate", borderSpacing: 0, fontSize: 13, color: "#3a3849" }}>
                            <colgroup>
                                <col style={{ width: 52 }} />
                                <col />
                                <col style={{ width: 110 }} />
                                <col style={{ width: 110 }} />
                                <col style={{ width: 110 }} />
                                <col style={{ width: 110 }} />
                                <col style={{ width: 180 }} />
                                <col style={{ width: 64 }} />
                                <col style={{ width: 200 }} />
                                <col style={{ width: 90 }} />
                            </colgroup>
                            <thead>
                                <tr style={{ background: "#f7f6fb" }}>
                                    {[
                                        { label: "번호", key: null, align: "center" },
                                        { label: "프로젝트 명", key: "projectName", align: "left" },
                                        { label: "계약시작일", key: "contractualStartDate", align: "center" },
                                        { label: "계약종료일", key: "contractualEndDate", align: "center" },
                                        { label: "실제시작일", key: "startDate", align: "center" },
                                        { label: "실제종료일", key: "endDate", align: "center" },
                                        { label: "고객사", key: "client", align: "left" },
                                        { label: "인원", key: "assignedPersonnelCount", align: "center" },
                                        { label: "설명", key: null, align: "left" },
                                        { label: "상태", key: null, align: "center" },
                                    ].map(({ label, key, align }) => (
                                        <th key={label}
                                            onClick={key ? () => applySort(key) : undefined}
                                            style={{
                                                padding: "12px 12px",
                                                fontSize: 12, fontWeight: 600, color: "#5d5b6e",
                                                textAlign: align,
                                                borderBottom: "1px solid #ececf2",
                                                whiteSpace: "nowrap", letterSpacing: ".02em",
                                                cursor: key ? "pointer" : "default",
                                                userSelect: key ? "none" : "auto",
                                            }}
                                        >
                                            {label}{key ? getSortIcon(key) : ""}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {currentItems?.map((project, index) => {
                                    const isEnded = project.status === '종료';
                                    const isMine = myProjectIds.has(project.projectId);
                                    const rowBg = isEnded ? "#f9f9f9" : isMine ? "#eef4fb" : "white";
                                    const rowHover = isEnded ? "#f4f4f4" : isMine ? "#e5eef9" : "#fafaff";
                                    return (
                                        <tr key={project.projectId || index}
                                            style={{ background: rowBg, transition: "background 100ms" }}
                                            onMouseEnter={(e) => e.currentTarget.style.background = rowHover}
                                            onMouseLeave={(e) => e.currentTarget.style.background = rowBg}
                                        >
                                            <td style={td("center")}>
                                                <span style={{ color: "#8b8a99" }}>{(currentPage - 1) * 10 + index + 1}</span>
                                            </td>
                                            <td style={{ ...td("left"), maxWidth: 340 }}>
                                                <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                                                    {isMine && (
                                                        <span style={{
                                                            fontSize: 9.5, fontWeight: 700, color: "#2c7dd0",
                                                            background: "#d8e6f6", padding: "2px 6px", borderRadius: 4,
                                                            letterSpacing: ".04em", whiteSpace: "nowrap", flexShrink: 0,
                                                        }}>MY</span>
                                                    )}
                                                    <span
                                                        onClick={() => navigation('/project/edit', { state: project })}
                                                        style={{
                                                            color: PURPLE, fontWeight: 600,
                                                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                                            cursor: "pointer", display: "block", minWidth: 0,
                                                        }}
                                                        onMouseEnter={(e) => { e.target.style.textDecoration = "underline"; }}
                                                        onMouseLeave={(e) => { e.target.style.textDecoration = "none"; }}
                                                        title={project.projectName}
                                                    >
                                                        {project.projectName}
                                                    </span>
                                                </div>
                                            </td>
                                            <td style={td("center", true)}>
                                                {project.contractualStartDate ? format(new Date(project.contractualStartDate), "yyyy-MM-dd") : "-"}
                                            </td>
                                            <td style={td("center", true)}>
                                                {project.contractualEndDate ? format(new Date(project.contractualEndDate), "yyyy-MM-dd") : "-"}
                                                {project.extended && (
                                                    <span style={{ marginLeft: 4, fontSize: 10.5, fontWeight: 700, color: "#7c3aed" }}>(연장)</span>
                                                )}
                                            </td>
                                            <td style={td("center", true)}>
                                                {project.startDate ? format(new Date(project.startDate), "yyyy-MM-dd") : "-"}
                                            </td>
                                            <td style={td("center", true)}>
                                                <span style={{ color: project.endDate ? "#3a3849" : "#c7c5d2" }}>
                                                    {project.endDate ? format(new Date(project.endDate), "yyyy-MM-dd") : "-"}
                                                </span>
                                            </td>
                                            <td style={{ ...td("left"), color: "#3a3849", maxWidth: 200 }}>
                                                <div title={project.client || ""} style={{ maxWidth: 200, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                    {project.client}
                                                </div>
                                            </td>
                                            <td style={td("center")}>
                                                <span style={{
                                                    fontSize: 12, color: "#5d5b6e", fontWeight: 500,
                                                    background: "#f4f3f9", padding: "2px 8px", borderRadius: 6,
                                                }}>
                                                    {project.assignedPersonnelCount}명
                                                </span>
                                            </td>
                                            <td style={{ ...td("left"), color: project.content ? "#3a3849" : "#c7c5d2", fontSize: 12.5, maxWidth: 280 }}>
                                                <div
                                                    title={project.content || ""}
                                                    style={{ maxWidth: 280, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                                                >
                                                    {project.content || "—"}
                                                </div>
                                            </td>
                                            <td style={td("center")}>
                                                <StatusBadge status={project.status} />
                                            </td>
                                        </tr>
                                    );
                                })}
                                {(!currentItems || currentItems.length === 0) && (
                                    <tr>
                                        <td colSpan={10} style={{ padding: "48px 20px", textAlign: "center", color: "#aaa9b7", fontSize: 13 }}>
                                            조건에 맞는 프로젝트가 없습니다.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div style={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        padding: "14px 20px", borderTop: "1px solid #ececf2",
                        background: "#fafaff",
                    }}>
                        <span style={{ fontSize: 12, color: "#8b8a99" }}>
                            전체 {projectList.length}건
                        </span>
                        {totalPages > 1 && (
                            <div style={{ display: "flex", gap: 4 }}>
                                <button
                                    onClick={handlePrevGroup}
                                    disabled={startPage === 1}
                                    style={{
                                        width: 30, height: 30, borderRadius: 7, border: "1px solid #ececf2",
                                        background: "white", color: startPage === 1 ? "#d0cfdb" : "#5d5b6e",
                                        fontSize: 16, cursor: startPage === 1 ? "not-allowed" : "pointer",
                                        fontFamily: "inherit", display: "inline-flex", alignItems: "center", justifyContent: "center",
                                    }}
                                >‹</button>
                                {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map(num => {
                                    const active = num === currentPage;
                                    return (
                                        <button key={num}
                                            onClick={() => handlePageChange(num)}
                                            style={{
                                                width: 30, height: 30, borderRadius: 7,
                                                border: active ? "none" : "1px solid #ececf2",
                                                background: active ? `linear-gradient(135deg, ${PURPLE}, ${MINT})` : "white",
                                                color: active ? "white" : "#5d5b6e",
                                                fontSize: 12.5, fontWeight: active ? 600 : 500,
                                                cursor: "pointer", fontFamily: "inherit",
                                            }}
                                        >{num}</button>
                                    );
                                })}
                                <button
                                    onClick={handleNextGroup}
                                    disabled={endPage >= totalPages}
                                    style={{
                                        width: 30, height: 30, borderRadius: 7, border: "1px solid #ececf2",
                                        background: "white", color: endPage >= totalPages ? "#d0cfdb" : "#5d5b6e",
                                        fontSize: 16, cursor: endPage >= totalPages ? "not-allowed" : "pointer",
                                        fontFamily: "inherit", display: "inline-flex", alignItems: "center", justifyContent: "center",
                                    }}
                                >›</button>
                            </div>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
}
